import { useEffect, useRef, useState, type TouchEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRightIcon, EarthIcon } from "../../assets/icons";
import { useLanguage } from "../../hooks/useLanguage";
import { useThemeBarColors } from "../../hooks/useThemeBarColors";
import "./WelcomeOnboarding.scss";

// Where the welcome flow hands the user off (Explore Plans / Skip).
const NEXT_DESTINATION = "/featured-plans";

const SWIPE_THRESHOLD = 50;

// Dark immersive status/bottom bar color for the welcome screen.
const WELCOME_BAR_COLOR = "#151515";

/**
 * Immersive four-screen welcome flow for anonymous Rejeem users on mobile.
 * Replaces the legacy "Manage Your Eating Habits" carousel.
 */
export default function WelcomeOnboarding() {
  const { t } = useTranslation("mobileIndex");
  const { language, isRTL, changeLanguage } = useLanguage();
  const navigate = useNavigate();

  // Dark status + bottom bars while the immersive welcome flow is on screen;
  // restored to the defaults (primary status, white bottom) on unmount.
  useThemeBarColors(WELCOME_BAR_COLOR, WELCOME_BAR_COLOR);

  const [currentSlide, setCurrentSlide] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // The four onboarding screens, content sourced from the mobileIndex i18n
  // namespace and background photos from /public/rejeem.
  const slides = [
    {
      background: "/rejeem/onboarding-1.jpg",
      badge: t("onboardingBadge1"),
      title: t("onboardingTitle1"),
      description: t("onboardingDescription1"),
    },
    {
      background: "/rejeem/onboarding-2.jpg",
      badge: t("onboardingBadge2"),
      title: t("onboardingTitle2"),
      description: t("onboardingDescription2"),
    },
    {
      background: "/rejeem/onboarding-3.jpg",
      badge: t("onboardingBadge3"),
      title: t("onboardingTitle3"),
      description: t("onboardingDescription3"),
    },
    {
      background: "/rejeem/onboarding-4.jpg",
      badge: t("onboardingBadge4"),
      title: t("onboardingTitle4"),
      description: t("onboardingDescription4"),
    },
  ];

  const lastIndex = slides.length - 1;
  const isLastSlide = currentSlide === lastIndex;
  const activeSlide = slides[currentSlide];

  // Keep the page backdrop dark while the immersive flow is mounted.
  useEffect(() => {
    document.body.classList.add("welcome-onboarding-active");
    return () => document.body.classList.remove("welcome-onboarding-active");
  }, []);

  const goToPlans = () => navigate(NEXT_DESTINATION);

  const goToSlide = (index: number) => {
    setCurrentSlide(Math.min(Math.max(index, 0), lastIndex));
  };

  const handleContinue = () => {
    if (isLastSlide) {
      goToPlans();
      return;
    }
    goToSlide(currentSlide + 1);
  };

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    const distance = touchStartX.current - touchEndX.current;
    if (Math.abs(distance) < SWIPE_THRESHOLD) return;
    // Slides progress right-to-left in RTL, so invert the gesture.
    const swipedForward = isRTL ? distance < 0 : distance > 0;
    goToSlide(currentSlide + (swipedForward ? 1 : -1));
  };

  const toggleLanguage = () => changeLanguage(language === "ar" ? "en" : "ar");

  return (
    <div
      className="welcome-onboarding"
      role="region"
      aria-label={activeSlide.title}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`welcome-onboarding__bg ${index === currentSlide ? "is-active" : ""}`}
          style={{ backgroundImage: `url(${slide.background})` }}
          aria-hidden
        />
      ))}
      <div className="welcome-onboarding__overlay" aria-hidden />

      <button type="button" className="welcome-onboarding__lang" onClick={toggleLanguage}>
        <EarthIcon />
        <span>{language === "ar" ? t("arabic") : t("english")}</span>
      </button>

      <div className="welcome-onboarding__card">
        <div className="welcome-onboarding__content" key={currentSlide}>
          <span className="welcome-onboarding__badge">
            <span className="welcome-onboarding__badge-dot" />
            {activeSlide.badge}
          </span>

          <div className="welcome-onboarding__text">
            <div className="welcome-onboarding__heading">
              <h1 className="welcome-onboarding__title">{activeSlide.title}</h1>
              <p className="welcome-onboarding__description">{activeSlide.description}</p>
            </div>

            <div className="welcome-onboarding__dots">
              {slides.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`welcome-onboarding__dot ${index === currentSlide ? "is-active" : ""}`}
                  onClick={() => goToSlide(index)}
                  aria-label={t("onboardingStep", { number: index + 1 })}
                  aria-current={index === currentSlide}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="welcome-onboarding__buttons">
          {!isLastSlide && (
            <button
              type="button"
              className="welcome-onboarding__btn welcome-onboarding__btn--skip"
              onClick={goToPlans}
            >
              {t("skip")}
            </button>
          )}
          <button
            type="button"
            className={`welcome-onboarding__btn welcome-onboarding__btn--primary ${
              isLastSlide ? "is-full" : ""
            }`}
            onClick={handleContinue}
          >
            <span>{isLastSlide ? t("explorePlans") : t("continue")}</span>
            <ChevronRightIcon className="welcome-onboarding__btn-icon" />
          </button>
        </div>
      </div>
    </div>
  );
}
